import { Fragment, useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Link } from 'react-router-dom';
import { StatusPill } from '../../components/StatusPills';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';

const pctOrDash = (v) => (v === null || v === undefined ? '—' : `${v}%`);

const rateTone = (v, { high = 20 } = {}) => {
  if (v === null || v === undefined) return 'neutral';
  return v >= high ? 'breach' : 'neutral';
};

const DIMENSIONS = [
  { value: 'ethnicity', label: 'Ethnicity' },
  { value: 'gender', label: 'Gender' },
  { value: 'age_band', label: 'Age range' },
  { value: 'disability', label: 'Disability' },
];

const PlatformSummary = () => {
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminApi.demographicsSummary().then((res) => !cancelled && setSummary(res.data));
    return () => { cancelled = true; };
  }, []);

  if (!summary) return null;
  const sections = [
    { key: 'ethnicity', label: 'Ethnicity' },
    { key: 'gender', label: 'Gender' },
    { key: 'age_band', label: 'Age range' },
    { key: 'disability', label: 'Disability' },
  ];
  const anyData = sections.some((s) => (summary[s.key] || []).length > 0);

  return (
    <Card>
      <CardContent className="p-5">
        <p className="font-medium text-foreground">Contributor demographics (platform-wide)</p>
        <p className="text-xs text-muted-foreground mt-0.5 mb-3">
          From the separate, opt-in demographic contribution. A category only appears once
          enough people have answered it.
        </p>
        {!anyData ? (
          <p className="text-sm text-muted-foreground">Not enough contributors yet.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {sections.map((s) => (
              <div key={s.key}>
                <p className="pd-eyebrow mb-1.5">{s.label}</p>
                {(summary[s.key] || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">Not enough data yet</p>
                ) : (
                  <ul className="space-y-1">
                    {summary[s.key].map((row) => (
                      <li key={row.category} className="flex items-center justify-between text-sm gap-2">
                        <span className="text-foreground truncate">{row.category}</span>
                        <span className="text-muted-foreground flex-shrink-0">{row.contributor_count}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DemographicBreakdownPanel = ({ orgId }) => {
  const [dimension, setDimension] = useState('ethnicity');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminApi.orgDemographicBreakdown(orgId, dimension)
      .then((res) => !cancelled && setData(res.data))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [orgId, dimension]);

  const findings = data?.findings_by_category || [];
  const outcomes = data?.outcomes_by_category || [];

  return (
    <div className="p-4 bg-ground rounded-lg space-y-4" data-testid={`demographics-panel-${orgId}`}>
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Break down by</span>
        <Select value={dimension} onValueChange={setDimension}>
          <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="demographics-dimension-trigger">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIMENSIONS.map((d) => (
              <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {data && (
          <span className="text-xs text-muted-foreground">
            Categories shown only once {data.min_contributors}+ people have contributed to them.
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : findings.length === 0 && outcomes.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No category has reached the minimum contributor threshold yet.
        </p>
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          {findings.length > 0 && (
            <div>
              <p className="text-xs font-medium text-foreground mb-1.5">Redaction / comparison findings</p>
              <div className="space-y-1.5">
                {findings.map((row) => (
                  <div key={row.category} className="flex items-center justify-between text-sm bg-surface rounded-md px-3 py-2 gap-2">
                    <span className="text-foreground truncate">{row.category}</span>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <StatusPill tone={rateTone(row.disproportionate_rate_pct)} size="sm">
                        {pctOrDash(row.disproportionate_rate_pct)} disproportionate
                      </StatusPill>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {outcomes.length > 0 && (
            <div>
              <p className="text-xs font-medium text-foreground mb-1.5">Placement / timing outcomes</p>
              <div className="space-y-1.5">
                {outcomes.map((row) => (
                  <div key={row.category} className="flex items-center justify-between text-sm bg-surface rounded-md px-3 py-2 gap-2">
                    <span className="text-foreground truncate">{row.category}</span>
                    <span className="text-muted-foreground flex-shrink-0">
                      {row.avg_placement_moves ?? '—'} moves avg
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminLeagueTable = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .leagueTable()
      .then((res) => !cancelled && setData(res.data))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const rows = data?.organisations || [];

  return (
    <div className="space-y-6" data-testid="admin-league-table">
      <div>
        <h1 className="font-heading text-2xl font-semibold text-foreground">League table</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
          Redaction and copy-comparison findings aggregated across every user who has opted in to
          contribute anonymised stats. Only pre-aggregated counts are ever written — never
          text, never a record or document id. A council only appears here once at least{' '}
          {data?.min_contributors ?? 5} distinct users have contributed to it, so no row can
          be traced back to a single person's request. Admin-only for now: these are
          AI-inferred legality judgments about named public bodies, not established findings.
        </p>
      </div>

      <PlatformSummary />

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <p className="text-sm text-muted-foreground p-5">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground p-5">
              Nothing to show yet — no organisation has reached the minimum contributor
              threshold.
            </p>
          ) : (
            <Table data-testid="league-table-rows">
              <TableHeader>
                <TableRow>
                  <TableHead>Organisation</TableHead>
                  <TableHead>Contributors</TableHead>
                  <TableHead>Documents</TableHead>
                  <TableHead>Redaction rate</TableHead>
                  <TableHead>Comparison runs</TableHead>
                  <TableHead>Disproportionate</TableHead>
                  <TableHead>Originator withheld</TableHead>
                  <TableHead>Challenge recommended</TableHead>
                  <TableHead>Words restored</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <Fragment key={r.canonical_org_id}>
                    <TableRow data-testid={`league-row-${r.canonical_org_id}`}>
                      <TableCell>
                        <Link
                          to={`/admin/organisations/${r.canonical_org_id}`}
                          className="font-medium text-foreground hover:underline"
                        >
                          {r.name}
                        </Link>
                        <div className="text-xs text-muted-foreground">{r.type}</div>
                      </TableCell>
                      <TableCell>{r.contributor_count}</TableCell>
                      <TableCell>{r.documents_observed}</TableCell>
                      <TableCell>
                        <StatusPill tone={rateTone(r.redaction_rate_pct, { high: 50 })} size="sm">
                          {pctOrDash(r.redaction_rate_pct)}
                        </StatusPill>
                      </TableCell>
                      <TableCell>{r.radar_runs}</TableCell>
                      <TableCell>
                        <StatusPill tone={rateTone(r.disproportionate_rate_pct)} size="sm">
                          {pctOrDash(r.disproportionate_rate_pct)}
                        </StatusPill>
                      </TableCell>
                      <TableCell>
                        <StatusPill tone={rateTone(r.originator_withheld_rate_pct)} size="sm">
                          {pctOrDash(r.originator_withheld_rate_pct)}
                        </StatusPill>
                      </TableCell>
                      <TableCell>
                        <StatusPill tone={rateTone(r.challenge_recommended_rate_pct, { high: 30 })} size="sm">
                          {pctOrDash(r.challenge_recommended_rate_pct)}
                        </StatusPill>
                      </TableCell>
                      <TableCell>{r.words_restored}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExpanded(expanded === r.canonical_org_id ? null : r.canonical_org_id)}
                          data-testid={`league-row-expand-${r.canonical_org_id}`}
                        >
                          Demographics
                          {expanded === r.canonical_org_id
                            ? <ChevronUp className="w-4 h-4 ml-1" />
                            : <ChevronDown className="w-4 h-4 ml-1" />}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {expanded === r.canonical_org_id && (
                      <TableRow>
                        <TableCell colSpan={10} className="p-3">
                          <DemographicBreakdownPanel orgId={r.canonical_org_id} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLeagueTable;
